import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { Subscription } from 'rxjs';

import { TranslatePipe } from 'app/core/i18n/translate.pipe';

import { DocumentGenerateService } from '../../document-generate.service';
import { ProductSearchDialogData } from '../../interfaces/dialog-data.interface';
import { DgNumberPipe } from '../../dg-number.pipe';
import { altUnitsOf } from '../../alt-units.util';
import { LineAltUnits } from '../../interfaces/dialog-data.interface';
import { BomCellComponent } from '../../components/bom-cell/bom-cell.component';

// unidad de medida tal como llega del buscador de materiales
interface SearchUnitOfMeasure {
  _id?: string;
  name?: string;
  hasacceptedDecimals?: boolean;
  decimalsAllowed?: number;
}

// lo que el modal necesita de cada material del resultado; el resto del archivo
// sigue trabajando con la respuesta cruda
export interface SearchMaterial {
  _id?: string;
  baseUnitOfMeasure?: SearchUnitOfMeasure;
  alternalUnitOfMeasures?: { unitOfMeasure?: SearchUnitOfMeasure; isUMB?: boolean }[];
  hasPrice?: boolean;
}

@Component({
  selector: 'app-product-search-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    ScrollingModule,
    DgNumberPipe,
    BomCellComponent,
    TranslatePipe,
  ],
  templateUrl: './product-search-modal.component.html',
})
export class ProductSearchModalComponent implements OnInit, OnDestroy {
  @ViewChild('resultsScroll') resultsScroll?: ElementRef<HTMLElement>;
  @ViewChild('cardsScroll') cardsScroll?: ElementRef<HTMLElement>;

  search = '';
  materials: any[] = [];
  selected = new Map<string, any>();

  readonly pageSize = 10;
  page = 1;
  total = 0;
  loading = false;
  // unidad de venta elegida por material (por defecto la UMB, o la primera vendible)
  private selectedUnit = new Map<string, string>();
  // cantidad indicada por material; vacío equivale a 1
  private selectedQty = new Map<string, number | null>();
  // vista de seleccionados en lugar del listado de búsqueda
  showingSelected = false;
  // texto del autocomplete de unidad por material
  private unitSearch = new Map<string, string>();

  // en pantallas de poca altura (móvil horizontal) el encabezado no deja ver los resultados
  shortScreen = false;

  private subs: Subscription[] = [];

  constructor(
    private dialogRef: MatDialogRef<ProductSearchModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ProductSearchDialogData,
    private service: DocumentGenerateService,
    private breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit(): void {
    this.search = this.data.initialSearch ?? '';
    this.subs.push(
      this.breakpointObserver.observe('(max-height: 560px)').subscribe((state) => {
        this.shortScreen = state.matches;
      })
    );
    this.fetch();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  onSearchEnter(): void {
    this.load();
  }

  private load(): void {
    this.page = 1;
    this.fetch();
  }

  private fetch(): void {
    this.loading = true;
    this.service
      .findDocumentGeneratorMaterials({
        documentClassId: this.data.documentClassId,
        warehouseId: this.data.warehouseId,
        salesOfficeId: this.data.salesOfficeId,
        businessPartnerId: this.data.businessPartnerId,
        currencyId: this.data.currencyId,
        search: this.search ?? '',
        page: this.page,
        size: this.pageSize,
      })
      .subscribe({
        next: (res) => {
          // el precio ya viene embebido en cada material (m.price)
          this.materials = res?.data?.data ?? [];
          this.total = res?.data?.pageInfo?.total ?? 0;
          this.loading = false;
        },
        error: () => {
          this.materials = [];
          this.total = 0;
          this.loading = false;
        },
      });
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  changePage(delta: number): void {
    const next = this.page + delta;
    if (next < 1 || next > this.totalPages || this.loading) return;
    this.page = next;
    this.resultsScroll?.nativeElement.scrollTo({ top: 0 });
    this.cardsScroll?.nativeElement.scrollTo({ top: 0 });
    this.fetch();
  }

  price(m: any): number {
    return m.__price ?? m.price ?? 0;
  }

  // código con ceros a la izquierda removidos si la clase de documento lo pide
  code(m: any): string {
    return this.data.removeLeftZeros ? String(m.code ?? '').replace(/^0+/, '') : m.code;
  }

  private key(m: any): string {
    return m._id?.toString();
  }

  isSelected(m: any): boolean {
    return this.selected.has(this.key(m));
  }

  // sin precio en la unidad base: no se puede seleccionar (en cualquier modo)
  selectable(m: any): boolean {
    return m?.hasPrice !== false;
  }

  // unidades vendibles del material, ordenadas base → secundaria → paralela → resto
  unitOptions(m: any): { id: string; name: string; chip: string | null }[] {
    const aums: any[] = m.alternalUnitOfMeasures ?? [];
    const umpActive = this.data.umpActive !== false;
    const options = aums
      .filter((a) => a?.unitOfMeasure && a?.isCommercialUom !== false && (umpActive || a?.isUMP !== true))
      .map((a) => ({
        id: a.unitOfMeasure._id?.toString(),
        name: a.unitOfMeasure.name ?? a.unitOfMeasure.code ?? '',
        chip: a.isUMB ? 'BASE' : a.isSecondaryAlternativeUom ? 'SECUNDARIA' : a.isUMP ? 'PARALELA' : null,
      }));
    // asegura la UMB como opción si no vino entre las alternas
    const umbId = m.baseUnitOfMeasure?._id?.toString();
    if (umbId && this.umbSellable(m) && !options.some((o) => o.id === umbId)) {
      options.unshift({ id: umbId, name: this.umb(m), chip: 'BASE' });
    }
    return options.sort((x, y) => this.chipRank(x.chip) - this.chipRank(y.chip));
  }

  private chipRank(chip: string | null): number {
    return chip === 'BASE' ? 0 : chip === 'SECUNDARIA' ? 1 : chip === 'PARALELA' ? 2 : 3;
  }

  // unidad por defecto: la UMB si es vendible; si no, la primera vendible del listado
  private defaultUnitId(m: any): string {
    const options = this.unitOptions(m);
    const umbId = m.baseUnitOfMeasure?._id?.toString();
    if (umbId && options.some((o) => o.id === umbId)) return umbId;
    return options[0]?.id ?? '';
  }

  // unidad de venta seleccionada por el usuario (o la por defecto)
  unitId(m: any): string {
    return this.selectedUnit.get(this.key(m)) ?? this.defaultUnitId(m);
  }

  unitName(m: any, id: string): string {
    return this.unitOptions(m).find((o) => o.id === id)?.name ?? '';
  }

  // texto mostrado en el autocomplete (nombre de la unidad, sin chip)
  unitSearchValue(m: any): string {
    const key = this.key(m);
    return this.unitSearch.has(key) ? this.unitSearch.get(key)! : this.unitName(m, this.unitId(m));
  }

  onUnitInput(m: any, text: string): void {
    this.unitSearch.set(this.key(m), text ?? '');
  }

  // la opción usa el nombre como valor (evita que el autocomplete escriba el id en el input)
  onUnitPick(m: any, name: string): void {
    const opt = this.unitOptions(m).find((o) => o.name === name);
    if (!opt) return;
    this.selectedUnit.set(this.key(m), opt.id);
    this.unitSearch.set(this.key(m), opt.name);
  }

  // al salir del campo: si el texto no coincide con una unidad, se restablece a la elegida
  onUnitBlur(m: any): void {
    this.unitSearch.set(this.key(m), this.unitName(m, this.unitId(m)));
  }

  // opciones filtradas por lo que el usuario escribe
  filteredUnits(m: any): { id: string; name: string; chip: string | null }[] {
    const options = this.unitOptions(m);
    const text = (this.unitSearch.get(this.key(m)) ?? '').toLowerCase().trim();
    const selectedName = this.unitName(m, this.unitId(m)).toLowerCase();
    if (!text || text === selectedName) return options;
    return options.filter((o) => o.name.toLowerCase().includes(text));
  }

  // unidad base, secundaria y paralela del material. Sin cantidad tecleada solo se
  // nombran; en cuanto el usuario escribe una, se convierten desde la unidad elegida
  altUnits(m: any): LineAltUnits {
    return altUnitsOf(m, this.unitId(m), this.quantityOf(m) ?? 0, this.data.umpActive !== false);
  }

  // hay cantidad que convertir; si no, la línea muestra únicamente el nombre de la unidad
  hasQuantity(m: any): boolean {
    return (this.quantityOf(m) ?? 0) > 0;
  }

  // stock disponible expresado en la unidad seleccionada (el stock se guarda en unidad base)
  stockInUnit(m: any): number {
    const base = this.stock(m);
    const aum = (m.alternalUnitOfMeasures ?? []).find(
      (a: any) => a?.unitOfMeasure?._id?.toString() === this.unitId(m)
    );
    const factor = Number(aum?.conversionFactor ?? aum?.equivalentQuantity ?? 1) || 1;
    return factor > 0 ? base / factor : base;
  }

  toggle(m: any): void {
    if (!this.selectable(m)) return;
    const id = this.key(m);
    if (this.selected.has(id)) {
      this.selected.delete(id);
      this.selectedQty.delete(id);
      return;
    }
    this.selected.set(id, m);
  }

  allSelected(): boolean {
    const selectables = this.materials.filter((m) => this.selectable(m));
    return selectables.length > 0 && selectables.every((m) => this.selected.has(this.key(m)));
  }

  toggleAll(checked: boolean): void {
    this.materials
      .filter((m) => this.selectable(m))
      .forEach((m) => {
        if (checked) {
          this.selected.set(this.key(m), m);
          return;
        }
        this.selected.delete(this.key(m));
        this.selectedQty.delete(this.key(m));
      });
  }

  // el listado puede traer un registro por almacén: se muestra solo el del documento
  stock(m: any): number {
    const warehouseId = this.data.warehouseId;
    return (m.materialStock || [])
      .filter((x: any) => !warehouseId || x.warehouseId?.toString() === warehouseId)
      .reduce((sum: number, x: any) => sum + (x.availableStock || 0), 0);
  }

  category(m: any): string {
    return m.materialGroups?.[0]?.name ?? '';
  }

  umb(m: any): string {
    return m.baseUnitOfMeasure?.name ?? m.baseUnitOfMeasure?.code ?? '-';
  }

  // la UMB vive en alternalUnitOfMeasures con isUMB: true, y ahí está la bandera de venta
  // del material. baseUnitOfMeasure.isCommercialUom es la del catálogo de unidades, no la
  // del material. Sin esa entrada no se bloquea: se asume vendible
  umbSellable(m: any): boolean {
    const umb = (m.alternalUnitOfMeasures ?? []).find((a: any) => a?.isUMB === true);
    return !umb || umb.isCommercialUom !== false;
  }

  // cantidad indicada; el campo vacío se entiende como 1
  quantityOf(m: SearchMaterial): number | null {
    return this.selectedQty.get(this.key(m)) ?? null;
  }

  // lo que se ve en el input; vacio mientras el usuario no indique cantidad
  quantityText(m: SearchMaterial): string {
    const value = this.selectedQty.get(this.key(m));
    return value == null ? '' : String(value);
  }

  setQuantity(m: SearchMaterial, value: string): void {
    const id = this.key(m);
    const raw = String(value ?? '').replace(',', '.');
    if (raw.trim() === '') {
      this.selectedQty.delete(id);
      return;
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    this.selectedQty.set(id, this.limitDecimals(m, Math.max(0, parsed)));
    // escribir una cantidad da por seleccionado el articulo: ahorra el clic del checkbox
    if (this.selectable(m) && !this.selected.has(id)) this.selected.set(id, m);
  }

  // la cantidad respeta los decimales de la unidad de venta elegida
  private limitDecimals(m: SearchMaterial, value: number): number {
    const uom =
      (m.alternalUnitOfMeasures ?? []).find(
        (a) => a?.unitOfMeasure?._id?.toString() === this.unitId(m)
      )?.unitOfMeasure ?? m.baseUnitOfMeasure;
    const accepts = uom?.hasacceptedDecimals === true;
    const decimals = Number(uom?.decimalsAllowed ?? 0) || 0;
    if (!accepts || decimals <= 0) return Math.floor(value);
    const factor = Math.pow(10, decimals);
    return Math.floor(value * factor) / factor;
  }

  selectedList(): SearchMaterial[] {
    return Array.from(this.selected.values());
  }

  selectedCount(): number {
    return this.selected.size;
  }

  toggleSelectedView(): void {
    this.showingSelected = !this.showingSelected;
  }

  removeSelected(m: any): void {
    const id = this.key(m);
    this.selected.delete(id);
    this.selectedQty.delete(id);
    if (!this.selected.size) this.showingSelected = false;
  }

  confirm(): void {
    // se adjunta la unidad de venta y la cantidad indicada en cada material seleccionado
    const chosen = Array.from(this.selected.values()).map((m) => ({
      ...m,
      __unitId: this.unitId(m),
      __quantity: this.quantityOf(m) ?? 1,
    }));
    this.dialogRef.close(chosen);
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
